<mj-page-layout>
    <mj-page-body>
        <!--
          Product states (PD §5): 3 skeleton cards on the first load (P/proto-states.js:128); the error panel when Home fails. A reload
          keeps the page and swaps only the picks for skeletons, with the sub line "Refreshing your matches…" (:128).
        -->
        @if (loading && !home) {
            <agu-skeleton [Count]="3" />
        } @else if (error) {
            <agu-state-panel
                Variant="error"
                [Icon]="ErrorCopy.Icon"
                [Title]="ErrorCopy.Title"
                [Body]="ErrorCopy.Body"
                PrimaryLabel="Try again"
                [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
                [SecondaryHref]="failures.SupportHref"
                [Foot]="failures.Foot"
                [Note]="failures.Note"
                (Primary)="Retry()"
            />
        } @else if (home && greeting) {
            <!--
              The mockup's `.top`: no header band, no eyebrow, and NO separate
              date element — the date is the first thing the greeting's own sub
              line says ("Today is Wednesday, 19 August 2026 · 6 new matches …",
              parity/home.md §4.2), so binding [Date] here as well would print it
              twice. The stat chip (emoji + stat, P/Member_Hub.html:307-312,1055)
              sits inline with the h1; the greeting prose,
              which carries bold counts and real navigation buttons, is projected
              as the rich sub line.
            -->
            <agu-plain-header [Title]="greeting.heading">
                @if (greeting.emoji) {
                    <span
                        tag
                        class="ra-home__gstat"
                        [class.ra-home__gstat--welcome]="greeting.tagKind === 'welcome'"
                        [class.ra-home__gstat--away]="greeting.tagKind === 'away'"
                        [class.ra-home__gstat--milestone]="greeting.tagKind === 'milestone'"
                    >
                        <span class="ra-home__gstat-emoji" aria-hidden="true">{{ greeting.emoji }}</span>
                        @if (greeting.tag) {
                            <span class="ra-home__gstat-text">{{ greeting.tag }}</span>
                        }
                    </span>
                }
                <p sub class="ra-home__greet-sub">
                    @for (part of greeting.bodyParts; track $index) {
                        @if (part.linkModuleKey) {
                            <button type="button" class="ra-home__greet-link" (click)="OnGreetingLinkClick(part.linkModuleKey)">{{ part.text }}</button>
                        } @else if (part.strong) {
                            <strong>{{ part.text }}</strong>
                        } @else {
                            {{ part.text }}
                        }
                    }
                </p>
            </agu-plain-header>

            <!-- A returning visit's "Since your last visit" card, before the dashboard (proto-states.js:88). "Continue: {role}" rides in its actions. -->
            @if (since; as card) {
                <section class="ra-home__since" aria-label="Since your last visit">
                    <div>
                        <span class="ra-home__since-k">{{ card.Title }}</span>
                        <span class="ra-home__since-t">{{ card.Text }}</span>
                    </div>
                    <div class="ra-home__since-acts">
                        @for (action of card.Actions; track action.Action) {
                            <button type="button" class="ra-home__btn" (click)="OnSinceAction(action.Action)">{{ action.Label }}</button>
                        }
                    </div>
                </section>
            }

            @if (!hasModules) {
                <!--
                  Reachable when the dashboard is the only module a user is entitled
                  to. Honest rather than a page of nothing.
                -->
                <agu-state-panel
                    Icon="lock"
                    Title="No modules available yet"
                    Body="Your account doesn't yet have access to any Research Accelerator modules. Contact your AGU administrator if you expected one."
                />
            } @else if (tileCards.length === 0) {
                <!--
                  Entitled to modules, but none of them takes a dashboard tile —
                  a Home + Saved + Settings composition is a legal UIConfigDetails
                  set. Say so rather than dropping the whole band silently.
                -->
                <agu-state-panel
                    Icon="inbox"
                    Title="Nothing on your dashboard yet"
                    Body="None of the modules you have access to has a dashboard tile. Use the sidebar to reach them."
                />
            } @else {
                <section class="ra-home__dash" aria-labelledby="ra-home-dash-h">
                    <div class="ra-home__dash-head">
                        <h2 id="ra-home-dash-h" class="ra-home__dash-eyebrow">Your research dashboard</h2>
                        <span class="ra-home__dash-sub">{{ loading ? RefreshingSub : 'Matched to your profile · refreshed ' + refreshedDate }}</span>
                    </div>
                    <!-- Partial failure (P/proto-states.js:111): before the tiles, naming the tile that did not refresh. -->
                    @if (partial; as p) {
                        <agu-state-banner Tone="error" [Text]="p.Lead" ActionLabel="Try again" [Note]="partialFailures.Note" (Action)="Retry()"><b>{{ p.Failed }}</b>.</agu-state-banner>
                    }
                    <div class="ra-home__tiles">
                        @for (card of tileCards; track card.module.Key) {
                            <article class="ra-home__tile" [attr.data-accent]="card.accent">
                                <div class="ra-home__tile-head">
                                    <span class="ra-home__tile-icon" aria-hidden="true">
                                        @if (card.module.IconName) {
                                            <agu-icon [Name]="card.module.IconName" [Size]="15" />
                                        } @else {
                                            <i [class]="card.module.Icon"></i>
                                        }
                                    </span>
                                    <h3 class="ra-home__tile-title">{{ card.title }}</h3>
                                    @if (card.pillText) {
                                        @if (card.pillVariant === 'done') {
                                            <!-- P/Member_Hub.html:1261-1262: `CV<span class="lg"> added</span>`, aria-label "CV added"; the " added" drops under 290px (:350). -->
                                            <span class="ra-home__tile-pill ra-home__tile-pill--done" aria-label="CV added">
                                                <i class="ra-home__tile-pill-dot ra-home__tile-pill-check" aria-hidden="true"></i>
                                                CV<span class="ra-home__tile-pill-lg"> added</span>
                                            </span>
                                        } @else {
                                            <span class="ra-home__tile-pill" [class.ra-home__tile-pill--pending]="card.pillVariant === 'pending'" [class.ra-home__tile-pill--failed]="card.pillVariant === 'failed'">
                                                <i class="ra-home__tile-pill-dot" aria-hidden="true"></i>
                                                {{ card.pillText }}
                                            </span>
                                        }
                                    }
                                </div>
                                <p class="ra-home__tile-body">{{ card.body }}</p>
                                <div class="ra-home__tile-actions">
                                    <button type="button" class="ra-home__tile-primary" (click)="OnPrimaryClick(card)">
                                        @if (card.primaryIcon) {
                                            <agu-icon [Name]="card.primaryIcon" [Size]="13" />
                                        }
                                        {{ card.primaryLabel }}
                                    </button>
                                    @if (card.secondaryLabel) {
                                        <button type="button" class="ra-home__tile-secondary" (click)="OnSecondaryClick(card)">{{ card.secondaryLabel }}</button>
                                    } @else {
                                        <!-- Holds the secondary row's height when a tile has no secondary (0 recommendations), so every tile's primary sits on the same line across the band. -->
                                        <span class="ra-home__tile-spacer" aria-hidden="true">&nbsp;</span>
                                    }
                                </div>
                            </article>
                        }
                    </div>
                </section>
            }

            <!-- The Featured module, after the dashboard (P/Member_Hub.html:1494-1522); one creative per session, rotating across sessions. -->
            @if (Feature; as ad) { <agu-ad-slot [Placement]="ad" /> }

            @if (loading) {
                <agu-skeleton [Count]="3" />
            } @else if (homeQuiet) {
                <!-- First visit: "Get set up" in place of the picks (proto-states.js:73-77; the alerts step is not built). -->
                <section class="ra-home__setup" aria-labelledby="ra-home-setup-h">
                    <div class="ra-home__setup-head">
                        <h2 id="ra-home-setup-h">Get set up</h2>
                        <span class="ra-home__setup-bar" aria-hidden="true"><span [style.width]="setupPercent"></span></span>
                        <span class="ra-home__setup-count">{{ setupProgress }}</span>
                    </div>
                    <ol class="ra-home__setup-steps">
                        @for (step of setupSteps; track step.Label; let i = $index) {
                            <li class="ra-home__setup-step" [class.ra-home__setup-step--done]="step.Done">
                                <span class="ra-home__setup-disc" aria-hidden="true">
                                    @if (step.Done) {
                                        <i class="fa-solid fa-check"></i>
                                    } @else {
                                        {{ i + 1 }}
                                    }
                                </span>
                                <div>
                                    <b>{{ step.Label }}</b>
                                    <span>{{ step.Body }}</span>
                                </div>
                                @if (!step.Done && step.Action && step.ActionLabel) {
                                    <button
                                        type="button"
                                        class="ra-home__btn"
                                        [class.ra-home__btn--quiet]="step.Action === 'career-map'"
                                        (click)="OnSetupAction(step.Action)"
                                    >
                                        {{ step.ActionLabel }}
                                    </button>
                                }
                            </li>
                        }
                    </ol>
                </section>
            } @else if (caughtUp) {
                <!-- Returning with nothing new (P/proto-states.js:95), where the picks render. -->
                <agu-state-panel
                    [Icon]="CaughtUpCopy.Icon"
                    [Title]="CaughtUpCopy.Title"
                    [Body]="CaughtUpCopy.Body"
                    PrimaryLabel="Browse grants"
                    SecondaryLabel="Widen research areas"
                    (Primary)="OnBrowseGrants()"
                    (Secondary)="OnWidenResearchAreas()"
                />
            } @else {
                <section class="ra-home__picks" aria-labelledby="ra-home-picks-h">
                    <div class="ra-home__picks-head">
                        <p class="ra-home__picks-eyebrow">
                            <svg class="ra-home__picks-star" width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" /></svg>
                            This week's picks
                        </p>
                        <h2 id="ra-home-picks-h" class="ra-home__picks-title">Recommended for you</h2>
                        <p class="ra-home__picks-sub">Your strongest matches this week, whatever fits best, from any part of the hub</p>
                    </div>

                    @if (home.picks.mode === 'none') {
                        <agu-state-panel
                            Icon="spark"
                            Title="We can't match you yet"
                            Body="Search with a few sentences about your work or upload a CV."
                        />
                    } @else if (pickRows.length === 0) {
                        <agu-state-panel
                            Icon="inbox"
                            Title="Nothing new this week"
                            Body="Check back soon — we keep matching in the background."
                        />
                    } @else {
                        @for (row of pickRows; track row.kind + ':' + row.key) {
                            <article class="ra-home__pick" [attr.data-accent]="row.accent" (click)="OnPickRowClick($event, row)">
                                <span class="ra-home__pick-rail" aria-hidden="true"></span>
                                <span class="ra-home__pick-type">{{ row.kindLabel }}</span>
                                <div class="ra-home__pick-body">
                                    <button type="button" class="ra-home__pick-title" (click)="OnOpenPick(row)">{{ row.title }}</button>
                                    <p class="ra-home__pick-meta">{{ row.meta }}</p>
                                </div>
                                <div class="ra-home__pick-actions">
                                    <button type="button" class="ra-home__pick-open" [attr.aria-label]="'Open ' + row.title" (click)="OnOpenPick(row)">
                                        Open <span aria-hidden="true">→</span>
                                    </button>
                                    <button
                                        type="button"
                                        class="ra-home__pick-icon agu-hit-24"
                                        [attr.aria-pressed]="row.saved"
                                        [attr.aria-label]="row.saved ? 'Remove ' + row.title + ' from saved' : 'Save ' + row.title"
                                        (click)="OnSavePick(row, !row.saved)"
                                    >
                                        <agu-icon Name="saved" [Size]="15" />
                                    </button>
                                    <button type="button" class="ra-home__pick-icon agu-hit-24" [attr.aria-label]="'Not for me: ' + row.title" (click)="OnDismissPick(row)">
                                        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </article>
                        }
                    }
                </section>
            }

            @if (LastDismissed$ | async; as dismissed) {
                <mj-alert Variant="info" Size="sm" Role="status">
                    Hidden: <b>{{ dismissed.title }}</b>
                    <button type="button" mjButton variant="flat" class="agu-hit-24" actions (click)="Undo()">Undo</button>
                </mj-alert>
            }

            @if (ShowCvModule) {
                <section class="ra-home__cv" aria-labelledby="ra-home-cv-h">
                    <span class="ra-home__cv-icon" aria-hidden="true"><i class="fa-solid fa-file-arrow-up"></i></span>
                    <div class="ra-home__cv-text">
                        <h3 id="ra-home-cv-h">Your CV isn't on file yet</h3>
                        <p>
                            Members with a CV get matched on skills and experience, not just publications, it sharpens
                            every grant, people and job recommendation in the hub.
                        </p>
                    </div>
                    <button type="button" class="ra-home__cv-cta" (click)="OnUploadCv()">Upload CV / résumé</button>
                </section>
            }
        }
    </mj-page-body>
</mj-page-layout>
